import { useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { useLocation } from 'react-router-dom';
import { usePlaces } from '@/hooks/usePlaces';
import { PlaceCard } from '@/components/PlaceCard';
import { ClubCard } from '@/components/ClubCard';
import { MenuSheet } from '@/components/MenuSheet';
import { BABCOCK_NATIONAL_WGA } from '@/lib/communityClubs';
import { nationalDiningMenu } from '@/lib/nationalDiningMenus';
import { CatFitnessIcon, CatFoodIcon, CatGolfIcon, ChevronRightIcon, PhoneIcon } from '@/components/Icons';
import './babcock-national.css';

const stroke = (p: SVGProps<SVGSVGElement>) => ({
  viewBox: '0 0 24 24',
  width: 20,
  height: 20,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
  ...p,
});

const LeaguesIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...stroke(p)}>
    <circle cx="8" cy="8" r="2.2" />
    <circle cx="16" cy="9" r="1.7" />
    <path d="M3.8 19c.5-3 2.4-4.6 4.2-4.6s3.7 1.6 4.2 4.6" />
    <path d="M13 15.2c1-.4 2.2-.6 3.2-.5 1.6.2 2.8 1.4 3.4 3.6" />
  </svg>
);

const WgaIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...stroke(p)}>
    <circle cx="12" cy="12" r="8" />
    <path d="M8 10.2c1.1.5 2 .5 3.2 0M8.4 13.6c1.1.5 2.1.5 3.3 0M12.4 9.4c.9.5 1.7.5 2.6 0" />
  </svg>
);

const DressIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...stroke(p)}>
    <path d="M8 6.5 12 10l4-3.5 3 2.2-2 2.8V20H7v-8.5L5 8.7Z" />
  </svg>
);

const GuestsIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...stroke(p)}>
    <circle cx="10" cy="8" r="3" />
    <path d="M4.2 19.5a6 6 0 0 1 11.6 0" />
    <path d="M18 8v6M15 11h6" />
  </svg>
);

const UnpublishedIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...stroke(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
);

const AmenitiesIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...stroke(p)}>
    <rect x="3.5" y="3.5" width="7" height="7" rx="1" />
    <rect x="13.5" y="3.5" width="7" height="7" rx="1" />
    <rect x="3.5" y="13.5" width="7" height="7" rx="1" />
    <rect x="13.5" y="13.5" width="7" height="7" rx="1" />
  </svg>
);

type NationalIcon = ComponentType<SVGProps<SVGSVGElement>>;

interface NationalSectionDef {
  id: string;
  title: string;
  accent: string;
  icon: NationalIcon;
}

const NATIONAL_SECTIONS: NationalSectionDef[] = [
  { id: 'fitness', title: 'Fitness classes', accent: 'fitness', icon: CatFitnessIcon },
  { id: 'contacts', title: 'Contacts', accent: 'contacts', icon: PhoneIcon },
  { id: 'golf', title: 'Golf', accent: 'golf', icon: CatGolfIcon },
  { id: 'leagues', title: 'Leagues', accent: 'leagues', icon: LeaguesIcon },
  { id: 'wga', title: BABCOCK_NATIONAL_WGA.name, accent: 'wga', icon: WgaIcon },
  { id: 'dress', title: 'Dress code — two different standards', accent: 'dress', icon: DressIcon },
  { id: 'dining', title: 'Dining', accent: 'dining', icon: CatFoodIcon },
  { id: 'guests', title: 'Guests', accent: 'guests', icon: GuestsIcon },
  { id: 'unpublished', title: "What the club doesn't publish", accent: 'unpublished', icon: UnpublishedIcon },
  { id: 'amenities', title: 'Amenities', accent: 'amenities', icon: AmenitiesIcon },
];

const SECTION_IDS = new Set(NATIONAL_SECTIONS.map((section) => section.id));

function sectionIdFromHash(hash: string): string | null {
  let raw = hash.replace(/^#/, '');
  try {
    raw = decodeURIComponent(raw);
  } catch {
    /* keep the raw fragment */
  }
  if (raw === 'bnwga') return 'wga';
  return SECTION_IDS.has(raw) ? raw : null;
}

function IconChip({ icon: Icon }: { icon: NationalIcon }) {
  return (
    <span className="bn-chip" aria-hidden="true">
      <Icon width={20} height={20} strokeWidth={2} />
    </span>
  );
}

function NationalSection({
  id,
  title,
  accent,
  icon,
  open,
  onToggle,
  children,
}: NationalSectionDef & { open: boolean; onToggle: () => void; children: ReactNode }) {
  const panelId = `${id}-panel`;
  const headingId = `${id}-heading`;
  return (
    <section id={id} className={`bn-section bn-accent bn-accent--${accent}`} aria-labelledby={headingId}>
      <h2 id={headingId} className="bn-section__heading">
        <button type="button" className="bn-section__toggle" aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
          <IconChip icon={icon} />
          <span className="bn-section__title">{title}</span>
          <ChevronRightIcon className={open ? 'bn-chevron bn-chevron--open' : 'bn-chevron'} />
        </button>
      </h2>
      <div id={panelId} role="region" aria-labelledby={headingId} hidden={!open} className="bn-section__panel">
        {children}
      </div>
    </section>
  );
}

function scrollToSection(id: string) {
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView?.({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
    });
  });
}

/**
 * Babcock National — the community we live in.
 *
 * This page used to carry hedged prose written from unverified information
 * ("has been open to public play", cargo shorts "have been flagged as not
 * permitted", and a paragraph guessing that SWFL courses "typically"
 * schedule off-season closures). All of it is now replaced with what the
 * club actually publishes: the 2026 Membership Guide, the printed
 * scorecard, both dress-code notices, the June 2026 food-and-beverage
 * hours sheet and the September 2026 Elevation Health class timetable.
 *
 * Where the club contradicts itself — and it does, on the street address —
 * this page says so rather than silently picking one. Where nothing is
 * published at all, it says that too, because "call the shop" is a useful
 * answer and an invented opening time is not.
 *
 * Golf, league, guest, fee, practice, attire, and staff facts that the
 * BNGC Welcome Package 2026-27 changes come from that package only.
 * Head Golf Professional: Matt Gagnon, PGA.
 */

const CLASSES: Array<[string, string, string]> = [
  ['Mon', '8:30 AM', 'Strength & Conditioning — Upper Body'],
  ['Mon', '10:00 AM', 'Cardio Splash'],
  ['Tue', '8:30 AM', 'Pilates Sculpt'],
  ['Tue', '10:00 AM', 'Foundations Fitness 101'],
  ['Tue', '11:00 AM', 'Therapeutic Yoga'],
  ['Wed', '8:30 AM', 'Strength & Conditioning — Lower Body'],
  ['Wed', '10:00 AM', 'Cardio Splash'],
  ['Wed', '6:00 PM', 'Fusion Power Yoga'],
  ['Thu', '8:30 AM', 'Pilates Power'],
  ['Thu', '10:00 AM', 'The Playground'],
  ['Thu', '11:00 AM', 'Yoga'],
  ['Fri', '10:00 AM', 'Cardio Splash'],
  ['Sat', '8:30 AM', 'Training C.A.M.P.'],
  ['Sat', '10:00 AM', 'Cardio Splash'],
  ['Sat', '11:00 AM', 'Vinyasa Yoga Flow'],
];

const DINING: Array<[string, string]> = [
  ['The Enclave', 'Sun 11–8 · Mon 11–5 · Wed–Sat 11–8 · closed Tue'],
  ['Watershed Grill', '12–6 daily · closed Tue'],
  ['Poolside service', '12–4 (Sat from 11) · closed Tue'],
  ['Concession window', '8–4 · closed Tue'],
];

const FEE_SEASONS = ['Jan–Apr', 'May & Oct', 'Jun–Sep', 'Nov–Dec'] as const;

/** 2026 access fees, rest of year. Plus tax, except Member Course Access Fees. (p.4) */
const ACCESS_2026: Array<[string, string, string, string, string]> = [
  ['Member 18', '$30', '$30', '$30', '$30'],
  ['Member 9', '$20', '$20', '$20', '$20'],
  ['Member Walking', '$0', '$0', '$0', '$0'],
  ['Member Guest 18', '$79', '$55', '$45', '$79'],
  ['Member Guest 9', '$55', '$45', '$45', '$55'],
  ['Member Guest Walking', '$20', '$20', '$20', '$20'],
  ['Tenant Member 18', '$69', '$45', '$45', '$69'],
  ['Tenant Member 9', '$45', '$35', '$35', '$45'],
  ['Tenant Member Walking', '$0', '$0', '$0', '$0'],
  ['Tenant Guest 18', '$94', '$69', '$49', '$89'],
  ['Tenant Guest 9', '$69', '$59', '$49', '$64'],
  ['Tenant Guest Walking', '$30', '$30', '$30', '$30'],
];

/** 2027 access fees. Draft, pending board approval. (p.4) */
const ACCESS_2027: Array<[string, string, string, string, string]> = [
  ['Member 18', '$32', '$32', '$32', '$32'],
  ['Member 9', '$22', '$22', '$22', '$22'],
  ['Member Walking', '$0', '$0', '$0', '$0'],
  ['Member Guest 18', '$94', '$60', '$50', '$84'],
  ['Member Guest 9', '$55', '$45', '$45', '$55'],
  ['Member Guest Walking', '$25', '$25', '$25', '$25'],
  ['Tenant Member 18', '$71', '$47', '$47', '$71'],
  ['Tenant Member 9', '$47', '$37', '$37', '$47'],
  ['Tenant Member Walking', '$0', '$0', '$0', '$0'],
  ['Tenant Guest 18', '$104', '$74', '$54', '$94'],
  ['Tenant Guest 9', '$74', '$62', '$49', '$69'],
  ['Tenant Guest Walking', '$35', '$35', '$35', '$35'],
  ['Public 18', '$149 before 1 PM', '$79', '$79', '$105'],
  ['Public 18 (12–3 PM)', '$129 (1–3 PM)', '$65', '$65', '$85'],
  ['Public Twilight (after 3 PM)', '$94', '$50', '$50', 'n/a'],
  ['SFPGA Golf Pass', 'n/a', '$65 / $55 PGA HOPE Grad', '$65 / $55 PGA HOPE Grad', 'n/a'],
  ['Reciprocal', 'n/a', '$60', '$60', 'n/a'],
  ['Reciprocal Guest', 'n/a', '$70', '$70', 'n/a'],
];

const GOLF_SERVICES: Array<[string, string]> = [
  ['Golf Membership Transfer Fee', '$200 for 1 month • $300 for 2 months • $400 for 3–12 months'],
  ['Bag Storage Combo Deal', '$300 (includes annual handicap fee, annual bag storage, and annual range pass)'],
  ['Annual Bag Storage', '$200'],
  ['Range Pass, annual', '$300'],
  ['Range Pass, monthly', '$50'],
  ['Range, daily', '$10 • Guest $20 • Public $30'],
  ['Annual Handicap Fee', '$35'],
  ['Club rental', '18 holes $55 • 9 holes $40'],
  ['Annual Guest Pass Booklet', '$500 (subject to change), limit 1 per household'],
];

const GOLF_STAFF = [
  'Paul Fitzpatrick, PGA Director of Golf',
  'Matt Gagnon, PGA Head Golf Professional',
  'Michael Jones, Lead Assistant Golf Professional',
  'Steven Field, LCAM, Administrative Support',
];

function AccessFeeTable({ rows }: { rows: Array<[string, string, string, string, string]> }) {
  return (
    <div className="bn-fit" style={{ marginTop: 8 }}>
      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-line)' }}>
            <th style={{ padding: '6px 4px 6px 0', textAlign: 'left', width: '28%' }}>Access type</th>
            {FEE_SEASONS.map((season) => (
              <th key={season} style={{ padding: '6px 3px', textAlign: 'left', fontWeight: 600 }}>
                {season}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]} style={{ borderBottom: '1px solid var(--color-line)' }}>
              {row.map((cell, i) => (
                <td
                  key={`${row[0]}-${i}`}
                  style={{
                    padding: '6px 3px 6px 0',
                    fontWeight: i === 0 ? 600 : 400,
                    verticalAlign: 'top',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BabcockNationalPage() {
  const { places } = usePlaces('babcock_national');
  const { hash } = useLocation();
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const openedFromHash = useRef<string | null>(null);
  const sections = places && places.length > 0 ? NATIONAL_SECTIONS : NATIONAL_SECTIONS.filter((section) => section.id !== 'amenities');

  useEffect(() => {
    const id = sectionIdFromHash(hash);
    if (!id) return;
    if (id === 'amenities' && !(places && places.length > 0)) return;
    if (openedFromHash.current === hash) return;
    openedFromHash.current = hash;
    setOpen((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    scrollToSection(id);
  }, [hash, places]);

  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function bodyFor(id: string): ReactNode {
    switch (id) {
      case 'fitness':
        return <FitnessBody />;
      case 'contacts':
        return <ContactsBody />;
      case 'golf':
        return <GolfBody />;
      case 'leagues':
        return <LeaguesBody />;
      case 'wga':
        return <ClubCard club={BABCOCK_NATIONAL_WGA} embedded />;
      case 'dress':
        return <DressBody />;
      case 'dining':
        return <DiningBody />;
      case 'guests':
        return <GuestsBody />;
      case 'unpublished':
        return <UnpublishedBody />;
      case 'amenities':
        return places?.map((place) => <PlaceCard key={place.id} place={place} detailed />);
      default:
        return null;
    }
  }

  return (
    <div className="bn-page">
      <div className="page-header">
        <h1 className="page-title">Babcock National</h1>
        <p className="page-subtitle">Our community — golf, dining and amenities</p>
      </div>

      <div className="bn-sections">
        {sections.map((section) => (
          <NationalSection
            key={section.id}
            {...section}
            open={open.has(section.id)}
            onToggle={() => toggle(section.id)}
          >
            {bodyFor(section.id)}
          </NationalSection>
        ))}
      </div>

      <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 8, lineHeight: 1.5 }}>
        Sources: babcocknationalgc.com (course details, rates, dress code notice), babcocknationalhoa.com (amenities,
        dining, fitness, racquet sports, F&amp;B hours), the 2026 Membership Guide, the printed scorecard, and the
        September 2026 Elevation Health schedule. Golf, leagues, Chelsea tee times, cancellation, guests, practice
        facility, transfer and service fees, access-fee grids, lessons, attire and course rules, and golf staff:
        BNGC Welcome Package 2026-27. Verified 2 September 2026; package facts 28 September 2026.
      </p>
    </div>
  );
}

function FitnessBody() {
  return (
    <>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 10 }}>
          Run by Elevation Health, on-site and virtual. All 50 minutes, none on Sunday.
        </p>
        <div className="bn-fit">
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <tbody>
            {CLASSES.map(([day, time, name], i) => (
              <tr key={`${day}-${time}-${i}`} style={{ borderBottom: '1px solid var(--color-line)' }}>
                <td style={{ padding: '7px 8px 7px 0', fontWeight: 600, width: 38 }}>{day}</td>
                <td style={{ padding: '7px 8px 7px 0', whiteSpace: 'nowrap', color: 'var(--color-ink-muted)' }}>
                  {time}
                </td>
                <td style={{ padding: '7px 0' }}>{name}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 10, lineHeight: 1.5 }}>
          September 2026 schedule; booking is behind the Elevation Health member portal. Questions:
          babcockfitness@contactelevation.com
        </p>
    </>
  );
}

function ContactsBody() {
  return (
    <>
        <p style={{ fontSize: 14, lineHeight: 1.6 }}>
          One number reaches both the club and the HOA: <strong>(941) 347-2200</strong>. The HOA office shares the
          Golf Pro Shop building at 43111 Greenway Blvd — its hours change seasonally and are never published, so
          call before walking over. The Clubhouse and The Enclave are at 43091 Greenway Blvd.
        </p>
        <div className="action-row">
          <a className="action-btn" href="tel:9413472200">
            Club &amp; HOA
          </a>
          <a className="action-btn" href="https://www.babcocknationalgc.com" target="_blank" rel="noreferrer">
            Golf site
          </a>
          <a className="action-btn" href="https://www.babcocknationalhoa.com" target="_blank" rel="noreferrer">
            HOA site
          </a>
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 10, lineHeight: 1.5 }}>
          Community Association Manager is Mary Ann Miller, mary.miller@theiconteam.com. Use that address — the
          mailto link in the HOA site footer points to a different person.
        </p>
    </>
  );
}

function GolfBody() {
  return (
    <>
        <p style={{ fontSize: 14, lineHeight: 1.6 }}>
          An 18-hole Gordon Lewis course, par 72, opened February 2020. It plays 7,067 yards from the Grey tees
          across nine sets down to 4,493, so there is a sensible length for everyone.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          Membership is bundled with the property — the club does not sell outside memberships. Members request tee
          times up to <strong>30 days</strong> ahead through the Chelsea lottery, which processes
          6 days out; the tee sheet opens for normal booking at noon on the 6th day. The course is also open to public play, booked up to
          5 days ahead.
        </p>
        <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 10, lineHeight: 1.5 }}>
          Tee sheet opens at noon on the 6th day. Source: BNGC Welcome Package 2026-27.
        </p>
        <ul style={{ fontSize: 14, lineHeight: 1.7, paddingLeft: 18, marginTop: 8 }}>
          <li>
            Golf Members and Transfer Members can request tee times up to 30 days in advance through the Chelsea
            Lottery.
          </li>
          <li>
            Requests are processed 6 days in advance. The request period closes at 3:30 AM, 6 days out, and then
            Chelsea assigns tee times.
          </li>
          <li>The tee sheet opens for normal booking at noon on the 6th day before play.</li>
          <li>
            Chelsea tells you whether you got your requested time. If you land in the overflow, you have to book a
            time yourself. You are not automatically given the next available time.
          </li>
          <li>
            Cancel at least 24 hours before a booked tee time. A late cancellation or no-show is charged for that
            time. A no-show that breaks the 24-hour policy also adds a 1-point penalty.
          </li>
          <li>Players must be ready to start at their tee time or risk losing it.</li>
          <li>
            Book guests through the lottery system. Every tee time needs at least one member and can include up to
            three guests.
          </li>
          <li>
            How priority works: each player has a point total for the last 14 days. The group's points are
            averaged, and groups with a lower average usually get priority.
            <ul style={{ paddingLeft: 18, marginTop: 4 }}>
              <li>A lottery placement adds 1 point per player, plus 1 point to the host for each guest.</li>
              <li>
                A regular tee-time booking adds ½ point per player at the end of the day of play. League play and
                events count.
              </li>
              <li>Points expire on a 14-day rolling window.</li>
              <li>Your point total is in your Chelsea profile.</li>
            </ul>
          </li>
          <li>Walking tee times are booked the day of play.</li>
        </ul>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          Shop hours are 7 AM–5 PM daily. <strong>June–September the course, range and shop close on Tuesdays</strong>,
          and summer maintenance days run 7 AM–1 PM. The 2026 full closures are June 1–5, July 20–24 and Sept 8–11.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          <strong>Practice facility.</strong> Monday through Sunday, 7:00 AM to 5:00 PM. On days you play, warm-up is
          included in your fees and limited to 60 minutes before your tee time. On non-playing days, Golf and Tenant
          Members pay a $10 daily fee to practice. All players, including Range Pass holders, must check in at the
          Golf Shop before using the practice facilities.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          <strong>Golf staff.</strong> {GOLF_STAFF.join('. ')}. For everyday golf questions, start with the Golf Shop
          or the pro staff.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          <strong>2026 access fees, rest of year.</strong> Amounts are plus tax, except Member Course Access Fees.
        </p>
        <AccessFeeTable rows={ACCESS_2026} />
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 14 }}>
          <strong>Draft, pending board approval.</strong> 2027 access fees. 2027 Fees are subject to board approval
          and subject to change.
        </p>
        <AccessFeeTable rows={ACCESS_2027} />
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 14 }}>
          <strong>Transfer fee, optional fees, and golf services.</strong> Register for these in person at the Golf
          Shop.
        </p>
        <div className="bn-fit">
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <tbody>
            {GOLF_SERVICES.map(([name, rate]) => (
              <tr key={name} style={{ borderBottom: '1px solid var(--color-line)' }}>
                <td style={{ padding: '7px 8px 7px 0', fontWeight: 600, verticalAlign: 'top' }}>{name}</td>
                <td style={{ padding: '7px 0', color: 'var(--color-ink-muted)' }}>{rate}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 10, lineHeight: 1.5 }}>
          Bag storage is limited and based on availability. Outside Services can load your bag before you arrive,
          clean your clubs, and store your bag after your round.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          <strong>Lessons.</strong> 30 minutes $50. 60 minutes $90. Package of 3 one-hour lessons $230. Package of 5
          one-hour lessons $360. Playing lessons depend on instructor and tee-time availability. The Club also offers
          group lessons, single-topic clinics, and junior golf camps, taught by Professionals and Apprentices.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          <strong>Attire and course rules summary.</strong>
        </p>
        <ul style={{ fontSize: 14, lineHeight: 1.7, paddingLeft: 18, marginTop: 8 }}>
          <li>
            Proper golf attire is required on the course and at the practice facilities. Cargo shorts, denim, and tee
            shirts are strictly prohibited.
          </li>
          <li>
            You can't bring outside food or drinks onto Club premises; they must be bought from the Club. No
            personal coolers on the course (carts have coolers). All alcohol must be bought through Babcock National,
            per state law.
          </li>
          <li>
            Pets aren't allowed in the Clubhouse, Amenities Center, or on the golf course. Personal golf carts
            aren't allowed on the course or practice facilities.
          </li>
          <li>
            Don't approach or disturb alligators. Always use the designated drop zone on hole #13. The USGA Rules
            of Golf apply along with the Babcock National local rules.
          </li>
          <li>
            You have 4 hours for 18 holes. Pace is tracked by GPS, and staff may penalize groups that fall behind.
            Newer golfers are encouraged to play off-peak, from the forward tees.
          </li>
          <li>The 90° rule is normally in effect. Carts stay on the path on par 3s.</li>
          <li>
            Members may walk after 5:00 PM. Check in with the Golf Shop, carry a sand bottle, and book walking tee
            times the day of play.
          </li>
          <li>
            Repair ball marks and fill divots, rake bunkers, leave the green promptly, mark your scorecard at the next
            tee, and respect the private property around the course.
          </li>
        </ul>
        <div className="action-row">
          <a className="action-btn" href="tel:9413472200">
            Golf shop
          </a>
          <a
            className="action-btn"
            href="https://babcockgf.chelseareservations.com"
            target="_blank"
            rel="noreferrer"
          >
            Member tee times
          </a>
        </div>
    </>
  );
}

function LeaguesBody() {
  return (
    <>
        <p style={{ fontSize: 14, lineHeight: 1.6 }}>
          All four leagues run <strong>November to April</strong>. Register through Golf Genius (web or app) or the
          member website under Golf Leagues &amp; Events, then League Directory. Ask the Golf Shop for the current
          league link.
        </p>
        <ul style={{ fontSize: 14, lineHeight: 1.7, paddingLeft: 18, marginTop: 8 }}>
          <li>
            <strong>Men's Golf League (BNMGA)</strong> — Wednesdays, 7:30 AM. Regular golf fee + $5 prize pool.
          </li>
          <li>
            <strong>Women's 9-Hole League</strong> — Thursdays, 1:03 PM. Regular golf fee + $5 prize pool.
          </li>
          <li>
            <strong>BNWGA 18-Hole (Women's Golf Association)</strong> — Thursdays. See Golf Genius (the calendar
            mostly shows 8:06 AM tee times, with some 8:30 AM shotguns). $50 annual fee + regular golf fee + prize
            pool.
          </li>
          <li>
            <strong>Mixed 18-Hole League</strong> — Sundays, 8:06 AM (the calendar shows 8:30 AM shotguns in Jan to
            Mar 2027). Regular golf fee + $5 per 2-person team.
          </li>
        </ul>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          <strong>Formats on the 2026-27 calendar:</strong> Scramble, shamble, Stableford (individual and team), and
          best-ball variants (1 BB of 4, 2 BB of 4, 1-2-3 of 4). Skins &amp; Pins, Net Nassau, Low Gross/Low Net, and
          Point Quota. Lone Ranger, Cha Cha Cha, Beat the Pro, Odd/Even, and Best 9 Holes.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          <strong>League milestones:</strong> Opening day: Mixed kickoff Sun Nov 1. Men's Wed Nov 4. 9-Hole and
          BNWGA18 opening scrambles Thu Nov 5. Men's Member-Member Wed Nov 18. BNWGA18 Member-Member Jan 27 to
          28. BNWGA18 Member-Guest Thu Mar 11. Closing day: 9-Hole Thu Apr 8. BNWGA18 Closing Luncheon Thu Apr 15. The
          Mixed League continues on Sundays through May 23, 2027.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          <strong>Club events:</strong> Men's &amp; Ladies Club Championships Feb 24 to 25, 2027. Couples Club
          Championship Sun Mar 21. Member-Guest Apr 2 to 3. Mixed Member-Guest Sun Apr 18. Battle of Babcock Apr 23 to
          24.
        </p>
        <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 10, lineHeight: 1.5 }}>
          October 2026, before the season: Ladies 18/9 open play on Thursdays at 8:06 AM, Men's League on
          Wednesdays at 7:30 AM, and Mixed League on Sundays at 8:06 AM.
        </p>
    </>
  );
}

function DressBody() {
  return (
    <>
        <p style={{ fontSize: 14, lineHeight: 1.6 }}>
          This trips people up, so it is worth being precise. <strong>On the course and practice facility</strong>,
          prohibited: denim of any kind, <strong>cargo shorts and cargo pants</strong>, t-shirts, collarless shirts,
          gym shorts, tennis shorts, tank tops, mesh, cut-offs, flip flops and bathing attire. Cart riders who are
          not playing still need golf attire.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          <strong>Inside The Enclave from 4 PM</strong> the rules are different and looser: no athleisure or athletic
          wear, no t-shirts or muscle shirts, no hats, no cut-offs, no spiked shoes, no flip flops, and no{' '}
          <em>ripped or torn</em> denim — but plain denim is fine, and cargo shorts are not on the clubhouse list at
          all. Swimwear is prohibited inside the clubhouse at all times.
        </p>
    </>
  );
}

function DiningBody() {
  const [openName, setOpenName] = useState<string | null>(null);
  const [shownName, setShownName] = useState(DINING[0][0]);
  const shown = nationalDiningMenu(shownName);
  const closeMenu = useCallback(() => setOpenName(null), []);

  function openMenu(name: string) {
    setShownName(name);
    setOpenName(name);
  }

  return (
    <>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 10 }}>
          Everything is <strong>closed Tuesdays</strong>, and Monday is a short day at The Enclave with no dinner.
          Lunch runs 11–4:30, dinner 5–8.
        </p>
        <ul className="bn-dining-list">
          {DINING.map(([outlet, hours]) => (
            <li key={outlet} className="bn-dining-row">
              <div className="bn-dining-row__text">
                <div className="bn-dining-row__name">{outlet}</div>
                <div className="bn-dining-row__hours">{hours}</div>
              </div>
              <button
                type="button"
                className="bn-dining-menu"
                aria-haspopup="dialog"
                aria-label={`Menu for ${outlet}`}
                onClick={() => openMenu(outlet)}
              >
                Menu
              </button>
            </li>
          ))}
        </ul>
        <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 10, lineHeight: 1.5 }}>
          A 20% gratuity is added automatically to every check. The Watershed Grill is member-only. Reserve through
          the club app or the QR code on the posted hours sheet.
        </p>
        {shown && (
          <MenuSheet
            slug={shown.slug}
            name={shown.name}
            category="food"
            open={openName !== null}
            onClose={closeMenu}
            preset={shown.menu}
            chrome="national"
            linkLabel={shown.linkLabel}
            datedLabel={shown.datedLabel}
          />
        )}
    </>
  );
}

function GuestsBody() {
  return (
    <>
        <p style={{ fontSize: 14, lineHeight: 1.6 }}>
          On the golf course: book guests through the lottery system. Every tee time needs at least one member and can
          include up to <strong>three guests</strong>. Guest green fees are in the access-fee grids under Golf.
          Annual Guest Pass Booklet is $500 (subject to change), limit 1 per household. Register in person at the Golf
          Shop.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          At the gate, guests use the visitor lane with ID and our address; the guard needs them registered in
          advance. Transponders and amenity cards are issued at the HOA office and cannot be issued to rental cars.
        </p>
        <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 10, lineHeight: 1.5 }}>
          The 3-guest cap is written for the golf course specifically. The club does not publish guest rules for the
          pool or amenity centre — ask the HOA office rather than assuming the golf rule carries over.
        </p>
    </>
  );
}

function UnpublishedBody() {
  return (
    <>
        <p style={{ fontSize: 14, lineHeight: 1.6 }}>
          Worth knowing so you call instead of hunting: <strong>pool hours</strong>, lap-lane count and towel policy;{' '}
          <strong>fitness centre opening hours</strong> and equipment list; <strong>court counts</strong> for tennis,
          pickleball and bocce, plus lighting and open-play times (all behind the PlayByPoint login); the{' '}
          <strong>guardhouse direct number</strong>; and HOA office hours.
        </p>
        <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
          The BNGC Welcome Package 2026-27 names Matt Gagnon, PGA, as Head Golf Professional. Phone the Golf Shop if
          it matters.
        </p>
    </>
  );
}
